import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function LureDiscussions() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [baseLure, setBaseLure] = useState(null)
  const [threads, setThreads] = useState([])
  const [loading, setLoading] = useState(true)

  // New thread form
  const [showForm, setShowForm] = useState(searchParams.get('new') === '1')
  const [newTitle, setNewTitle] = useState('')
  const [newBody, setNewBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')

  useEffect(() => {
    fetchData()
  }, [id])

  const fetchData = async () => {
    try {
      const [{ data: lureData }, { data: threadData }] = await Promise.all([
        supabase
          .from('base_lures')
          .select('base_lure_id, name, brands(brand_name)')
          .eq('base_lure_id', id)
          .single(),
        supabase
          .from('forum_threads')
          .select('thread_id, title, created_at, updated_at, user_id, is_locked, forum_posts(post_id)')
          .eq('base_lure_id', id)
          .order('updated_at', { ascending: false }),
      ])

      setBaseLure(lureData)

      const userIds = [...new Set((threadData || []).map(t => t.user_id))]
      const { data: usersData } = userIds.length > 0
        ? await supabase.from('users').select('id, username').in('id', userIds)
        : { data: [] }

      const userMap = {}
      usersData?.forEach(u => { userMap[u.id] = u.username })

      setThreads((threadData || []).map(t => ({
        ...t,
        username: userMap[t.user_id] || 'Unknown',
        postCount: t.forum_posts?.length || 0,
      })))
      setLoading(false)
    } catch (err) {
      console.error('Error fetching discussions:', err)
      setLoading(false)
    }
  }

  const handleCreateThread = async () => {
    if (!newTitle.trim()) { setFormError('Title is required.'); return }
    if (!newBody.trim()) { setFormError('Opening message is required.'); return }
    setSubmitting(true)
    setFormError('')

    try {
      const { data: threadData, error: threadError } = await supabase
        .from('forum_threads')
        .insert([{ base_lure_id: parseInt(id), user_id: user.id, title: newTitle.trim() }])
        .select()
        .single()

      if (threadError) throw threadError

      const { error: postError } = await supabase
        .from('forum_posts')
        .insert([{ thread_id: threadData.thread_id, user_id: user.id, body: newBody.trim() }])

      if (postError) throw postError

      navigate(`/lure/${id}/discussions/${threadData.thread_id}`)
    } catch (err) {
      setFormError(err.message || 'Failed to create discussion.')
      setSubmitting(false)
    }
  }

  if (loading) return <div className="loading">Loading discussions...</div>

  return (
    <div className="forum-page">
      <div className="breadcrumb">
        <Link to="/browse">Browse</Link>
        {' → '}
        <Link to={`/lure/${id}`}>{baseLure?.name}</Link>
        {' → '}<span>Discussions</span>
      </div>

      <div className="forum-page-header">
        <div>
          <h1>Discussions</h1>
          <p className="page-subtitle">
            {baseLure?.name} — {baseLure?.brands?.brand_name}
          </p>
        </div>
        {user && !showForm && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + Start a Discussion
          </button>
        )}
      </div>

      {/* New thread form */}
      {showForm && (
        <div className="forum-new-thread-form">
          <h3>Start a New Discussion</h3>
          {formError && <div className="alert-error" style={{ marginBottom: '0.75rem' }}>{formError}</div>}

          <div className="form-group">
            <label><strong>Title *</strong></label>
            <input
              type="text"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              className="form-input"
              placeholder="e.g. Best retrieval technique for murky water?"
              maxLength={300}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label><strong>Opening Message *</strong></label>
            <textarea
              value={newBody}
              onChange={e => setNewBody(e.target.value)}
              className="form-textarea"
              rows="5"
              placeholder="Share your question, tip, or experience..."
            />
          </div>

          <div className="form-actions">
            <button
              className="btn-secondary"
              onClick={() => { setShowForm(false); setFormError(''); setNewTitle(''); setNewBody('') }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleCreateThread} disabled={submitting}>
              {submitting ? 'Posting...' : 'Post Discussion'}
            </button>
          </div>
        </div>
      )}

      {!user && (
        <p className="forum-login-note">
          <Link to="/login">Log in</Link> to start a discussion.
        </p>
      )}

      {/* Thread list */}
      {threads.length === 0 ? (
        <div className="no-content-box">
          <p>No discussions yet for this lure. Be the first to start one!</p>
        </div>
      ) : (
        <div className="forum-thread-list-full">
          <div className="forum-thread-list-header">
            <div className="ftl-title">Topic</div>
            <div className="ftl-meta">Started by</div>
            <div className="ftl-posts">Posts</div>
            <div className="ftl-activity">Last Activity</div>
          </div>

          {threads.map(thread => (
            <Link
              key={thread.thread_id}
              to={`/lure/${id}/discussions/${thread.thread_id}`}
              className="forum-thread-list-row"
            >
              <div className="ftl-title">
                <span className="ftl-thread-title">{thread.title}</span>
                {thread.is_locked && <span className="ftl-locked-badge">🔒 Locked</span>}
              </div>
              <div className="ftl-meta">{thread.username}</div>
              <div className="ftl-posts">{thread.postCount}</div>
              <div className="ftl-activity">{new Date(thread.updated_at).toLocaleDateString()}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default LureDiscussions
